import { useEffect, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import QueryIcon from 'assets/query.svg';
import PepWiseQueryBuilder from 'components/QueryBuilder';
import TableSection from './TableSection';
import { getFinopsResourceMappingList } from 'service/service';
import LoaderComponent from 'components/Loader';
import { useSearchParams } from 'react-router-dom';
import DashboardIcon from '../../../assets/material-symbols_dashboard.svg';
import PageWrapper from 'components/Common/PageWraper';
import { fetchFieldOptionsHelper, fetchKeyFieldsListData } from './ResourceMappingHelper';

const ResourceMapping = () => {
	const [searchParams, ] = useSearchParams();
	const [programId, setProgramId] = useState<any>('');
	const [projectId, setProjectId] = useState<any>('');
	const [programName, setProgramName] = useState<any>('');
	const [projectName, setProjectName] = useState<any>('');
	const [keyFields, setKeyFields] = useState({});
	const [loading, setLoading] = useState(true);
	const [isQueryPrefChange, setIsQueryPrefChange] = useState(false);
	const [valueField, ] = useState<any>({});
	const [totalPages, setTotalPages] = useState(0);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [isReapplied, setIsReapplied] = useState(false);
 	const abortControllerRef = useRef<AbortController | null>(null);
	const [isAbort, setIsAbort] = useState(false);
	const [resourceTypeCost, setResourceTypeCost] = useState<any>([]);

	const fetchFieldOptions = async (fieldKey: string) => {
		return fetchFieldOptionsHelper(
			fieldKey,
			queryPreference,
			setKeyFields
		);
	};

	const fetchOptionsByOperator = async (operator: string, fieldKey: string) => {
		try {
			if((operator === 'null') || (operator === 'notNull')) {
				const options: any[] = []; 
				setKeyFields((prevFields: any) => ({
					...prevFields,
					[fieldKey]: {
						...prevFields[fieldKey],
						fieldOptions: {
							...(prevFields[fieldKey]?.fieldOptions || {}),
							options
						}
					}
				}));
				return options;
			}
			else {
				const currentOptions = (keyFields as any)?.[fieldKey]?.fieldOptions?.options;
				if (!Array.isArray(currentOptions) || currentOptions.length === 0) { 
					return fetchFieldOptionsHelper(
						fieldKey,
						queryPreference,
						setKeyFields
					);
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	const fetchFields = async () => {
		setLoading(true);
		const { fieldsData, resourceTypeCost: initialCost } = await fetchKeyFieldsListData();
		setKeyFields(fieldsData);
		setResourceTypeCost(initialCost ?? []);
		setLoading(false);
	};

	useEffect(() => {
		// remove DOM manipulation after all components change to mui without css
		const activeId = document.getElementById('root');
		if (activeId) {
			document.head.classList.add('theme-bg-blue');
			document.body.classList.add('theme-bg-blue');
		}
		if (searchParams.get('programId')) 
			setProgramId(searchParams.get('programId'));
		if (searchParams.get('projectId')) 
			setProjectId(searchParams.get('projectId'));
		if (searchParams.get('programName')) 
			setProgramName(searchParams.get('programName')?.replace(/%20/g, ' '));
		if (searchParams.get('projectName')) 
			setProjectName(searchParams.get('projectName')?.replace(/%20/g, ' '));
		else
			setQueryProps({
				isSearch: false,
				query: ''
			});
		fetchFields();
	}, []);

	const [queryProps, setQueryProps] = useState({
		isSearch: false,
		query: ""
	});
	const [queryPreference, setQueryPreference] = useState('Resource_flat');
	const queryPreferenceOptions = [
		{
			label: 'Tree List of Resource Groups',
			value: 'ResourceGroup_tree'
		},
		{
			label: 'Flat List of Resources',
			value: 'Resource_flat'
		},
		{
			label: 'Tree List of DBSQL Clusters',
			value: 'SqlEndpointId_tree'
		},
		{
			label: 'Tree List of Interactive Clusters',
			value: 'ClusterName_inttree'
		},
		{
			label: 'Tree List of Job Clusters',
			value: 'ClusterJobSource_jobtree'
		},
		{
			label: 'Blank Query',
			value: 'Blank_flatall'
		}
	];
	
	return (
		<PageWrapper>
			{searchParams.get('programName') ? 
			<PageHeader
				icon={DashboardIcon}
				title={[
					{
						text: 'Programs',
						link: '/finops/resourcemapping/programs'
					},
					{
						text: programName,
						link: `/finops/resourcemapping/programs/${programId}/${encodeURIComponent(programName)}`
					},
					{
						text: projectName,
						link: `/finops/resourcemapping/projects/${programId}/${projectId}/${encodeURIComponent(programName)}/${encodeURIComponent(projectName)}`
					},
					{
						text: 'Search & Allocate'
					}
				]}
			/> : 
			<PageHeader title="Search & Allocate" icon={QueryIcon} />}
			<Stack p={2} spacing={2}>
				{loading ? (
					<LoaderComponent size={50} pt={10} />
				) : (
					<>
						<PepWiseQueryBuilder
							valueField={valueField}
							fields={keyFields}
							setQueryProps={setQueryProps}
							queryPreference={queryPreference}
							setQueryPreference={setQueryPreference}
							queryPreferenceOptions={queryPreferenceOptions}
							loadOptionsForField={fetchFieldOptions}
							fieldValueOptionsCallback={getFinopsResourceMappingList}
							setTotalPages = {setTotalPages}
							sortingInfo={sortingInfo} 
        					setSortingInfo={setSortingInfo}
        					setIsQueryPrefChange={setIsQueryPrefChange}
							setIsReapplied = {setIsReapplied}
							isReapplied = {isReapplied}
							loadOptionsForOperator={fetchOptionsByOperator}
							abortControllerRef = {abortControllerRef}
							setIsAbort = {setIsAbort}
							isAbort = {isAbort}
							resourceTypeCost = {resourceTypeCost}
						/>
						<TableSection queryPreference={queryPreference} fields={keyFields} queryProps={queryProps} isAddResource={true} totalPages= {totalPages} setTotalPages = {setTotalPages} sortingInfo={sortingInfo} 
        				setSortingInfo={setSortingInfo} isQueryPrefChange = {isQueryPrefChange} setIsQueryPrefChange={setIsQueryPrefChange} isReapplied = {isReapplied} abortControllerRef = {abortControllerRef} 
						setIsAbort = {setIsAbort} onResourceTypeCostChange={setResourceTypeCost}/>
					</>
				)}
			</Stack>
		</PageWrapper>
	);
};

export default ResourceMapping;
